// @ts-strict-ignore
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Link, Separator, Typography } from "@aviary";
import { Ingredient } from "@clinic/shared/components/AggregatedIngredientList/Ingredient/Ingredient";
import { IngredientAccordion } from "@clinic/shared/components/AggregatedIngredientList/IngredientAccordion/IngredientAccordion";
import type { AggregatedIngredient } from "@clinic/shared/components/IngredientTable/AggregatedIngredients.query";
import type { DailyIntake } from "@clinic/shared/data/splinters/DailyIntake.fragment";
import { l } from "@clinic/shared/locales/i18n";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import type { PlanIngredient } from "@shared/types/graphqlGenerated";

import * as styles from "./AggregatedIngredientList.styles";

const COLLAPSED_INGREDIENT_COUNT = 4;
const INGREDIENT_GROUP_HEIGHT = 144;

interface Props {
  aggregatedDailyIntake: AggregatedIngredient[] | DailyIntake[] | PlanIngredient[];
  withIndent?: boolean;
  showProductIcon?: boolean;
  isForDailyIntake?: boolean;
  isExpandedByDefault?: boolean;
}

const AggregatedIngredientList = ({
  aggregatedDailyIntake,
  withIndent,
  showProductIcon,
  isForDailyIntake,
  isExpandedByDefault = false,
}: Props) => {
  const { t } = useTranslation();
  const ingredientGroupRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault);
  const [isGummiesMedicationEnabled] = useFlippers("aura_medication_ingredient_intake");
  const { phoneLarge } = useBreakpoints();
  const isPhone = phoneLarge.lessThan;

  const handleClose = () => {
    setIsExpanded(false);

    if (isForDailyIntake) {
      window.scrollTo({ top: 120, behavior: "smooth" });
    } else {
      const groupRoof =
        ingredientGroupRef?.current?.getBoundingClientRect()?.top +
        window.pageYOffset -
        INGREDIENT_GROUP_HEIGHT;
      window.scrollTo({ top: groupRoof, behavior: "smooth" });
    }
  };

  const collapsedList = aggregatedDailyIntake.slice(0, COLLAPSED_INGREDIENT_COUNT - 1);
  const listToShow = isExpanded ? aggregatedDailyIntake : collapsedList;
  const numberIngredientsNotInCollapsedView = aggregatedDailyIntake.length - collapsedList.length;

  const renderHeader = () => {
    if (!isGummiesMedicationEnabled) return null;

    return (
      <>
        <div css={styles.header}>
          <Typography isMarginless css={styles.ingredient}>
            {t(l.common.Ingredient)}
          </Typography>
          {!isPhone && !!showProductIcon && (
            <Typography isMarginless css={styles.products}>
              {t(l.common.Product)}
            </Typography>
          )}
          <Typography isMarginless css={styles.quantity}>
            {t(l.common.Quantity)}
          </Typography>
        </div>
        <Separator />
      </>
    );
  };

  if (aggregatedDailyIntake.length === 0) return null;

  return (
    <div ref={ingredientGroupRef}>
      {renderHeader()}
      {listToShow.map(ingredientWithDosage => {
        if (ingredientWithDosage.childIngredients?.length > 0) {
          return (
            <IngredientAccordion
              key={ingredientWithDosage.ingredient.id}
              ingredientWithDosage={ingredientWithDosage}
              showProductIcon={showProductIcon}
              withIndent={withIndent}
            />
          );
        }

        return (
          <Ingredient
            key={ingredientWithDosage.ingredient.id}
            name={ingredientWithDosage.ingredient.name}
            amount={ingredientWithDosage.dosage}
            unit={ingredientWithDosage.dosageUnit}
            alternateAmount={ingredientWithDosage.alternativeDosage}
            alternateUnit={ingredientWithDosage.alternativeDosageUnit}
            productNames={ingredientWithDosage.productNames}
            productCount={ingredientWithDosage.productCount}
            withIndent={withIndent}
            showProductIcon={showProductIcon}
          />
        );
      })}
      {!isExpanded && numberIngredientsNotInCollapsedView > 0 && (
        <div css={styles.link}>
          <Link isColor="system" onClick={() => setIsExpanded(true)} isUnderLined={false}>
            {t(l.common.ShowMoreIngredients)}
            <FontAwesomeIcon icon={faChevronDown} size="sm" css={styles.chevron} />
          </Link>
        </div>
      )}
      {!isExpandedByDefault && !!isExpanded && numberIngredientsNotInCollapsedView > 0 && (
        <div css={styles.link}>
          <Link isColor="system" onClick={handleClose} isUnderLined={false}>
            {t(l.common.ShowFewerIngredients)}
            <FontAwesomeIcon icon={faChevronUp} size="sm" css={styles.chevron} />
          </Link>
        </div>
      )}
    </div>
  );
};

export { AggregatedIngredientList };
